import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  Button,
  Input,
  Label,
  Textarea,
  Switch,
  toast,
} from '@ludolio/shared-ui';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { addGameDraftKey, clearFormDraft, getFormDraft, setFormDraft } from '@/lib/formDraftStorage';

export const Route = createFileRoute('/_authenticated/games/add')({
  component: AddGamePage,
});

type AddGameFormData = {
  title: string;
  description: string;
  publisher: string;
  version: string;
  iconUrl: string;
  bannerUrl: string;
  isDrmFree: boolean;
};

function AddGamePage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [draftReady, setDraftReady] = useState(false);
  const [formData, setFormData] = useState<AddGameFormData>({
    title: '',
    description: '',
    publisher: user?.developerCompanyName || '',
    version: '1.0.0',
    iconUrl: '',
    bannerUrl: '',
    isDrmFree: false,
  });

  const draftKey = user?.id ? addGameDraftKey(user.id) : null;

  useEffect(() => {
    if (!draftKey) {
      setDraftReady(true);
      return;
    }

    const draft = getFormDraft<AddGameFormData>(draftKey);
    if (draft) {
      setFormData(draft.data);
    }
    setDraftReady(true);
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey || !draftReady) return;

    const timer = window.setTimeout(() => {
      setFormDraft(draftKey, formData);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [draftKey, draftReady, formData]);

  // Redirect if not verified
  if (!user?.isVerifiedDeveloper) {
    return (
      <div className="space-y-6">
        <Link to="/games" className="inline-flex items-center text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Games
        </Link>
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-500" />
              <div>
                <p className="font-medium text-yellow-600 dark:text-yellow-400">Account Not Verified</p>
                <p className="text-sm text-muted-foreground">
                  Your developer account must be verified before you can submit games.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare submission data
      const submitData: {
        title: string;
        description?: string;
        publisher?: string;
        version?: string;
        iconUrl?: string;
        bannerUrl?: string;
        isDrmFree?: boolean;
      } = {
        title: formData.title,
        description: formData.description || undefined,
        publisher: formData.publisher || undefined,
        version: formData.version || undefined,
        isDrmFree: formData.isDrmFree,
      };

      // Add URL fields if provided
      if (formData.iconUrl) submitData.iconUrl = formData.iconUrl;
      if (formData.bannerUrl) submitData.bannerUrl = formData.bannerUrl;

      const response = await apiClient.submitGame(submitData);
      if (!response.success) {
        toast.error(response.message || 'Failed to create game');
        return;
      }

      const game = response.data;
      const failedMedia = new Set<string>();

      if (iconFile) {
        try {
          const iconRes = await apiClient.uploadGameMedia({ appId: game.appId, type: 'icon', file: iconFile });
          if (!iconRes.success) {
            failedMedia.add('icon');
          }
        } catch {
          failedMedia.add('icon');
        }
      }

      if (bannerFile) {
        try {
          const bannerRes = await apiClient.uploadGameMedia({ appId: game.appId, type: 'banner', file: bannerFile });
          if (!bannerRes.success) {
            failedMedia.add('banner');
          }
        } catch {
          failedMedia.add('banner');
        }
      }

      for (const file of galleryFiles) {
        try {
          const galleryRes = await apiClient.uploadGameMedia({ appId: game.appId, type: 'gallery', file });
          if (!galleryRes.success) {
            failedMedia.add('gallery images');
          }
        } catch {
          failedMedia.add('gallery images');
        }
      }

      if (draftKey) {
        clearFormDraft(draftKey);
      }

      if (failedMedia.size > 0) {
        toast.error(`Game created, but some media failed to upload (${Array.from(failedMedia).join(', ')}). Retry from the Media tab.`);
      } else {
        toast.success('Game created successfully!');
      }
      navigate({ to: '/games/$gameId', params: { gameId: game.id } });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/games" className="inline-flex items-center text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Games
      </Link>

      <Card>
        <CardHeader>
          <h1 className="text-2xl font-semibold leading-none tracking-tight">Create New Game</h1>
          <CardDescription>
            Create a new game entry. After filling in the details, you can upload your game build and submit for review.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Game Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="My Awesome Game"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your game..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="publisher">Publisher</Label>
                <Input
                  id="publisher"
                  value={formData.publisher}
                  onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                  placeholder="Your Studio Name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0.0"
                />
              </div>
            </div>



            {/* DRM-Free Toggle */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="isDrmFree">DRM-Free Game</Label>
                <p className="text-sm text-muted-foreground">
                  Check this if your game does not include Ludolio SDK integration.
                  DRM-free games can be played without session validation.
                </p>
              </div>
              <Switch
                id="isDrmFree"
                checked={formData.isDrmFree}
                onCheckedChange={(checked) => setFormData({ ...formData, isDrmFree: checked })}
              />
            </div>

            {/* Icon & Banner */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="iconFile">Icon (512 × 512)</Label>
                <Input
                  id="iconFile"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setIconFile(e.target.files?.[0] || null)}
                />
                <Label htmlFor="iconUrl" className="sr-only">Icon URL</Label>
                <Input
                  id="iconUrl"
                  value={formData.iconUrl}
                  onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
                  placeholder="https://.../icon.png"
                />
              </div>

              {/* Banner */}
              <div className="space-y-2">
                <Label htmlFor="bannerFile">Banner (landscape, minimum 1920 × 1080)</Label>
                <Input
                  id="bannerFile"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                />
                <Label htmlFor="bannerUrl" className="sr-only">Banner URL</Label>
                <Input
                  id="bannerUrl"
                  value={formData.bannerUrl}
                  onChange={(e) => setFormData({ ...formData, bannerUrl: e.target.value })}
                  placeholder="https://.../banner.jpg"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="galleryFiles">Gallery Screenshots</Label>
              <Input
                id="galleryFiles"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                aria-describedby="galleryFilesDescription"
                onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
              />
              <p id="galleryFilesDescription" className="text-sm text-muted-foreground">
                Select multiple gameplay screenshots or artwork to build a Steam-like image gallery.
              </p>
              {galleryFiles.length > 0 && (
                <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground" role="status" aria-live="polite">
                  {galleryFiles.length} image{galleryFiles.length === 1 ? '' : 's'} selected
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <Link to="/games">
                <Button type="button" variant="outline">Cancel</Button>
              </Link>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Game'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
